import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import api from "./api";

export default function Cases() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/crm/cases").then((r) => {
      setItems(r.data.items);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((c) =>
      [c.contact_name, c.product, c.company, c.status, c.policy_number]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(s))
    );
  }, [q, items]);

  const totalPremium = filtered.reduce(
    (a, c) => a + (c.annual_premium || 0),
    0
  );

  return (
    <div data-testid="cases-page">
      <header className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-amber-300/80">
            Policies
          </div>
          <h1 className="text-3xl font-light mt-1">Cases</h1>
        </div>
        <div className="text-right">
          <div className="text-xs text-white/50 uppercase tracking-widest">
            Total annual premium
          </div>
          <div className="text-xl font-light tabular-nums">
            ${Math.round(totalPremium).toLocaleString()}
          </div>
        </div>
      </header>

      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search cases by contact, product, company, policy #…"
            data-testid="cases-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-300/40"
          />
        </div>
        <div className="text-sm text-white/50">
          {filtered.length.toLocaleString()} of {items.length.toLocaleString()}
        </div>
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-widest text-white/50 border-b border-white/[0.06]">
              <th className="px-4 py-3 font-normal">Date</th>
              <th className="px-4 py-3 font-normal">Contact</th>
              <th className="px-4 py-3 font-normal hidden md:table-cell">Product</th>
              <th className="px-4 py-3 font-normal hidden md:table-cell">Company</th>
              <th className="px-4 py-3 font-normal hidden lg:table-cell">Agent</th>
              <th className="px-4 py-3 font-normal text-right">Premium</th>
              <th className="px-4 py-3 font-normal hidden lg:table-cell">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="p-6 text-center text-white/40">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-6 text-center text-white/40">
                  No cases.
                </td>
              </tr>
            ) : (
              filtered.slice(0, 500).map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-white/[0.04] hover:bg-white/[0.03]"
                  data-testid={`case-${c.case_id}`}
                >
                  <td className="px-4 py-3 text-white/70 whitespace-nowrap">
                    {c.case_date?.slice(0, 10)}
                  </td>
                  <td className="px-4 py-3">
                    {c.contact_id ? (
                      <Link
                        to={`/crm/contacts/${c.contact_id}`}
                        className="text-white hover:text-amber-200"
                      >
                        {c.contact_name || "—"}
                      </Link>
                    ) : (
                      c.contact_name || "—"
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-white/80">
                    {c.product || "—"}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-white/70">
                    {c.company || "—"}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-white/70">
                    {c.agent || "—"}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-white/90">
                    ${Math.round(c.annual_premium || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-white/70">
                    {c.status || "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
